import { useState, useMemo, useRef, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import useFilePermissions from "~/hooks/useFilePermissions";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";

interface WorkbenchOption {
  id: number;
  title: string;
}

interface WorkbenchSelectorProps {
  onSelect: (workbench: WorkbenchOption) => void;
  onCancel: () => void;
}

export function WorkbenchSelector({ onSelect, onCancel }: WorkbenchSelectorProps) {
  const [mode, setMode] = useState<"select" | "create">("select");
  const [selectedWorkbenchId, setSelectedWorkbenchId] = useState<string>("");
  const [newWorkbenchName, setNewWorkbenchName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  const { data: projects, isLoading: projectsLoading } = useProjectsAccessList();
  const { data: permissions, isLoading: permissionsLoading } = useFilePermissions();

  const loading = projectsLoading || permissionsLoading;

  useEffect(() => {
    // Find the dialog content element to use as portal container
    if (formRef.current) {
      const dialogContent = formRef.current.closest('[role="dialog"]');
      if (dialogContent) {
        setPortalContainer(dialogContent as HTMLElement);
      }
    }
  }, []);

  // Filter projects with write permissions
  const availableWorkbenches: WorkbenchOption[] = useMemo(() => {
    const pathRegex = /^research\/projects\/(?<projectId>\d+)\/\*/;
    if (projects && permissions) {
      // Look at DAC response for project file prefixes with write access
      const validProjectIds = Object.entries(permissions)
        .filter(([path, access]) => pathRegex.test(path) && access.includes("write"))
        .map(([path]) => Number(path.match(pathRegex)?.groups?.projectId));

      // Compare with RWB response for all projects a user has functional access to
      return projects
        .filter(({ project_id }) => validProjectIds.includes(project_id))
        .map(({ project_id, project_title }) => ({ id: project_id, title: project_title }))
        .sort((a, b) => a.title.localeCompare(b.title));
    }
    return [];
  }, [projects, permissions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (mode === "create") {
      setIsCreating(true);
      setCreateError(null);
      try {
        const formData = new FormData();
        formData.append("title", newWorkbenchName);

        const resp = await fetch(`${import.meta.env.VITE_RWB_API}/projects`, {
          method: "POST",
          body: formData,
        });

        if (!resp.ok) {
          throw new Error("Unable to create workbench");
        }

        // POST /projects returns the new project ID as a bare integer (response_model=int)
        const id: number = await resp.json();

        // Poll for DAC permissions to be ready
        const maxAttempts = 10;
        const pollInterval = 500; // 500ms between attempts
        let hasWritePermission = false;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          try {
            const permissionsResp = await fetch(
              `${import.meta.env.VITE_RWB_API}/file-browser/permissions`
            );
            if (permissionsResp.ok) {
              const permissions: Record<string, string[]> = await permissionsResp.json();
              // Check if user has write access to the new project
              const projectPath = `research/projects/${id}/*`;
              if (permissions[projectPath]?.includes("write")) {
                hasWritePermission = true;
                break;
              }
            }
          } catch (error) {
            console.warn("Error checking permissions:", error);
          }

          if (attempt < maxAttempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }
        }

        if (!hasWritePermission) {
          console.warn(
            "Permissions not confirmed after polling, proceeding anyway. Export may fail."
          );
        }

        // Create the /exports/ directory for the new workbench
        try {
          const exportsDir = `research/projects/${id}/exports/`;
          await fetch(`${import.meta.env.VITE_RWB_API}/file-browser/create-folder`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              folder_path: exportsDir,
            }),
          });
        } catch (error) {
          // Log but don't fail - the directory might already exist or will be created on upload
          console.warn("Could not pre-create exports directory:", error);
        }

        onSelect({ id, title: newWorkbenchName });
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to create workbench";
        setCreateError(message);
      } finally {
        setIsCreating(false);
      }
    } else {
      const selected = availableWorkbenches.find((w) => String(w.id) === selectedWorkbenchId);
      if (selected) {
        onSelect(selected);
      }
    }
  };

  const canSubmit = mode === "create" ? newWorkbenchName.trim() : selectedWorkbenchId;

  if (isCreating) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-3">
        <Loader2 className="size-8 text-primary animate-spin" />
        <p className="text-sm font-medium">Creating workbench...</p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Exported files may take a few minutes to appear in the workbench file browser.
      </p>
      <div className="space-y-2">
        <h3 className="text-sm font-medium">
          {mode === "select" ? "Select a workbench" : "Create a new workbench"}
        </h3>

        {mode === "select" ? (
          <Select value={selectedWorkbenchId} onValueChange={setSelectedWorkbenchId}>
            <SelectTrigger
              className="w-full"
              style={{
                borderRadius: "6px",
                border: "1px solid rgba(0, 0, 0, 0.23)",
              }}
            >
              <SelectValue placeholder="Choose a workbench..." />
            </SelectTrigger>
            <SelectContent
              container={portalContainer}
              className="rounded-xl bg-white"
              style={{
                boxShadow:
                  "0 11px 15px -7px rgba(0, 0, 0, 0.20), 0 24px 38px 3px rgba(0, 0, 0, 0.14), 0 9px 46px 8px rgba(0, 0, 0, 0.12)",
              }}
            >
              {loading ? (
                <SelectItem value="loading" disabled>
                  Loading workbenches...
                </SelectItem>
              ) : availableWorkbenches.length === 0 ? (
                <SelectItem value="empty" disabled>
                  No workbenches available
                </SelectItem>
              ) : (
                availableWorkbenches.map((workbench) => (
                  <SelectItem key={workbench.id} value={String(workbench.id)}>
                    {workbench.title}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        ) : (
          <Input
            type="text"
            placeholder="Workbench name..."
            value={newWorkbenchName}
            onChange={(e) => setNewWorkbenchName(e.target.value)}
            autoFocus
            style={{
              borderRadius: "6px",
              border: "1px solid rgba(0, 0, 0, 0.23)",
            }}
          />
        )}

        <button
          type="button"
          onClick={() => {
            setMode(mode === "select" ? "create" : "select");
            setSelectedWorkbenchId("");
            setNewWorkbenchName("");
          }}
          className="text-sm text-primary hover:underline"
        >
          {mode === "select" ? "Create a new workbench" : "Select a workbench"}
        </button>
      </div>

      {createError && <p className="text-sm text-destructive">{createError}</p>}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {mode === "create" ? "Create" : "Next"}
        </Button>
      </div>
    </form>
  );
}
